import React, { memo } from 'react'
import clsx from 'clsx'
import {
  Toolbar,
  Typography,
  Select,
  InputBase,
  MenuItem,
  useTheme,
  IconButton,
  Box,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  MdLastPage,
  MdFirstPage,
  MdKeyboardArrowRight,
  MdKeyboardArrowLeft,
} from 'react-icons/md'

const useStyles = makeStyles((theme) => ({
  pagination: {
    display: 'flex',
    flexDirection: 'row-reverse',
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
    overflow: 'auto',
    '&:last-child': {
      padding: 0,
    },
  },
  toolbar: {
    minHeight: 52,
    paddingRight: 2,
  },
  spacer: {
    flex: '1 1 100%',
  },
  caption: {
    flexShrink: 0,
  },
  selectRoot: {
    marginRight: 32,
    marginLeft: 8,
  },
  select: {
    paddingLeft: 8,
    paddingRight: 24,
    textAlign: 'right',
    textAlignLast: 'right',
  },
  input: {
    color: 'inherit',
    fontSize: 'inherit',
    flexShrink: 0,
  },
  actions: {
    flexShrink: 0,
    marginLeft: 20,
  },
  paginationActions: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}))

const handleChangePgAtual = (value, pages) => {
  if (value < 1) return 0
  else if (value > pages) return pages - 1
  return value - 1
}

const DefaultLabelDisplayedRows = ({
  pages,
  page,
  onChangePage,
  from,
  to,
  count,
  showCount,
}) => {
  return (
    <React.Fragment>
      <Box style={{ padding: '1px 0px', display: 'inline' }}>
        <InputBase
          inputProps={{
            type: 'number',
            min: 1,
            max: pages || 1,
            value: page + 1,
          }}
          onChange={(e) =>
            onChangePage(e, handleChangePgAtual(e.target.value, pages))
          }
        />
        {` / ${pages || 1}`}
      </Box>
      {showCount && (
        <div
          style={{ display: 'inline', marginLeft: '10px', paddingLeft: '10px' }}
        >
          {from} a {to} de {count}
        </div>
      )}
    </React.Fragment>
  )
}

// eslint-disable-next-line react/display-name
const TablePaginationActions = memo(
  ({ count, page, rowsPerPage, onChangePage }) => {
    const classes = useStyles()
    const theme = useTheme()
    const handleFirstPageButtonClick = (e) => onChangePage(e, 0)
    const handleBackButtonClick = (e) => onChangePage(e, page - 1)
    const handleNextButtonClick = (e) => onChangePage(e, page + 1)
    const handleLastPageButtonClick = (e) =>
      onChangePage(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
    return (
      <div className={classes.paginationActions}>
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
          {theme.direction === 'rtl' ? <MdLastPage /> : <MdFirstPage />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
          {theme.direction === 'rtl' ? (
            <MdKeyboardArrowRight />
          ) : (
            <MdKeyboardArrowLeft />
          )}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          {theme.direction === 'rtl' ? (
            <MdKeyboardArrowLeft />
          ) : (
            <MdKeyboardArrowRight />
          )}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        >
          {theme.direction === 'rtl' ? <MdFirstPage /> : <MdLastPage />}
        </IconButton>
      </div>
    )
  },
)

// eslint-disable-next-line react/display-name
const Pagination = memo(
  ({
    ActionsComponent = TablePaginationActions,
    // backIconButtonProps,
    // backIconButtonText = 'Página Anterior',
    className,
    count,
    LabelDisplayedRows = DefaultLabelDisplayedRows,
    labelRowsPerPage = 'Linhas',
    // nextIconButtonProps,
    // nextIconButtonText = 'Próxima Página',
    onChangePage,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
    rowsPerPageOptions = [10, 25, 50, 100],
    SelectProps = {},
    showCount,
  }) => {
    const classes = useStyles()
    const MenuItemComponent = SelectProps.native ? 'option' : MenuItem
    return (
      <div className={clsx(classes.pagination, className)}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.spacer} />
          {rowsPerPageOptions.length > 1 && (
            <Typography
              color="inherit"
              variant="body2"
              className={classes.caption}
            >
              {labelRowsPerPage}
            </Typography>
          )}
          {rowsPerPageOptions.length > 1 && (
            <Select
              classes={{
                select: classes.select,
                icon: classes.selectIcon,
              }}
              input={
                <InputBase
                  className={clsx(classes.input, classes.selectRoot)}
                />
              }
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              {...SelectProps}
            >
              {rowsPerPageOptions.map((rowsPerPageOption) => (
                <MenuItemComponent
                  className={classes.menuItem}
                  key={
                    rowsPerPageOption.value
                      ? rowsPerPageOption.value
                      : rowsPerPageOption
                  }
                  value={
                    rowsPerPageOption.value
                      ? rowsPerPageOption.value
                      : rowsPerPageOption
                  }
                >
                  {rowsPerPageOption.label
                    ? rowsPerPageOption.label
                    : rowsPerPageOption}
                </MenuItemComponent>
              ))}
            </Select>
          )}
          <Typography
            color="inherit"
            variant="body2"
            component="div"
            className={classes.caption}
          >
            <LabelDisplayedRows
              {...{
                pages: Math.ceil(count / parseInt(rowsPerPage, 10)),
                from: count === 0 ? 0 : page * rowsPerPage + 1,
                to:
                  count !== -1
                    ? Math.min(count, (page + 1) * rowsPerPage)
                    : (page + 1) * rowsPerPage,
                count: count === -1 ? -1 : count,
                page,
                onChangePage,
                showCount,
              }}
            />
          </Typography>
          <ActionsComponent
            className={classes.actions}
            // backIconButtonProps={{
            //   title: backIconButtonText,
            //   ...backIconButtonProps,
            // }}
            count={count}
            // nextIconButtonProps={{
            //   title: nextIconButtonText,
            //   ...nextIconButtonProps,
            // }}
            onChangePage={onChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
          />
        </Toolbar>
      </div>
    )
  },
)

export default Pagination
